import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Avatar } from '../shared/components/Avatar';
import * as Colors from '../theme/colors';

export const BAR_HEIGHT = 56;
const HAIRLINE = 'rgba(255,255,255,0.06)';

type Props = {
  label?: string;
  online?: boolean;
  status?: string;
  elapsed?: string;
  onEnd?: () => void;
  showFullscreen?: boolean;
  recording?: boolean;
  children?: React.ReactNode;
};

const SessionBar: React.FC<Props> = ({
  label,
  online,
  status,
  elapsed,
  onEnd,
  showFullscreen,
  recording,
  children,
}) => {
  const { user, logout } = useAuth0();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const name = user?.name || user?.email || '';

  return (
    <Box
      sx={{
        height: BAR_HEIGHT,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: Colors.Surface,
        borderBottom: `1px solid ${HAIRLINE}`,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
        <Box
          component="img"
          src="/zerac-logo.png"
          alt="Zerac Contractors"
          onClick={() => (navigate('/portal'))}
          sx={{ height: 22, cursor: 'pointer' }}
        />

        {label && <Typography sx={{ color: Colors.White, fontSize: 13 }}>{label}</Typography>}

        {(online !== undefined || status) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: online ? Colors.Green : Colors.HelperText,
              }}
            />
            <Typography sx={{ fontSize: 13, color: online ? Colors.Green : Colors.LightGray }}>
              {online ? 'Online' : status}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {recording && (
          <Box
            title="This session is being recorded"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            <FiberManualRecordIcon
              sx={{
                fontSize: 12,
                color: Colors.Red,
                animation: 'zrec 2s ease-in-out infinite',
                '@keyframes zrec': { '50%': { opacity: 0.35 } },
              }}
            />
            <Typography sx={{ fontSize: 13, color: Colors.Red }}>Recording</Typography>
          </Box>
        )}

        {children}

        {elapsed && (
          <Typography sx={{ color: Colors.LightGray, fontSize: 13 }}>{elapsed}</Typography>
        )}

        {onEnd && (
          <IconButton onClick={onEnd} title="End session" sx={{ color: Colors.Red }}>
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        {name ? (
          <Box onClick={(e) => setAnchor(e.currentTarget)} sx={{ cursor: 'pointer', display: 'flex' }}>
            <Avatar name={name} size="small" />
          </Box>
        ) : (
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: Colors.InputBackground }} />
        )}

        {showFullscreen && (
          <IconButton
            onClick={() => {
              if (document.fullscreenElement) document.exitFullscreen();
              else document.documentElement.requestFullscreen();
            }}
            title="Fullscreen"
            sx={{ color: Colors.LightGray }}
          >
            <FullscreenIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                minWidth: 220,
                bgcolor: Colors.Surface,
                border: `1px solid ${HAIRLINE}`,
                borderRadius: '10px',
              },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ color: Colors.White, fontSize: 14, fontWeight: 600 }}>
              {user?.name || 'Contractor'}
            </Typography>
            <Typography sx={{ color: Colors.HelperText, fontSize: 12 }}>{user?.email}</Typography>
          </Box>
          <Divider sx={{ borderColor: HAIRLINE }} />
          <MenuItem
            onClick={() => (navigate('/portal'))}
            sx={{ fontSize: 13, color: Colors.White }}
          >
            Dashboard
          </MenuItem>
          <MenuItem
            component="a"
            href="https://app.zerac.info"
            sx={{ fontSize: 13, color: Colors.White }}
          >
            Account
          </MenuItem>
          <MenuItem
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            sx={{ fontSize: 13, color: Colors.White }}
          >
            Sign Out
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default SessionBar;
